import React, { useState } from 'react';
import { Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/react';
import { useNearbyRooms } from '../hooks/useNearbyRooms';

const NearbyRoomsModal = ({ examSchedule, onRoomSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isSearching, error, nearbyRooms, findNearbyRooms, resetNearbyRooms } = useNearbyRooms(examSchedule);

  const handleOpen = () => {
    setIsOpen(true);
    // Start search when modal opens
    findNearbyRooms(500); // 500 meters search radius
  };

  const handleClose = () => {
    setIsOpen(false);
    resetNearbyRooms();
  };

  const handleRoomSelectAndClose = (building, room) => {
    onRoomSelect(building, room);
    setIsOpen(false);
  };

  return (
    <>
      <div className="flex justify-center my-4">
        <Button
          onPress={handleOpen}
          color="primary"
          className="bg-primary text-white hover:bg-primary-hover"
          startContent={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
          }
        >
          Find Nearby Available Rooms
        </Button>
      </div>

      <Modal 
        isOpen={isOpen} 
        onClose={handleClose}
        size="3xl"
        scrollBehavior="inside"
        classNames={{
          base: "bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 border border-gray-100 dark:border-gray-800",
          backdrop: "bg-black/50",
          header: "text-gray-900 dark:text-gray-100",
          body: "text-gray-800 dark:text-gray-200",
          footer: "border-t border-gray-100 dark:border-gray-800"
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 text-gray-900 dark:text-gray-100">
                Nearby Available Classrooms
              </ModalHeader>
              <ModalBody className="text-gray-800 dark:text-gray-200">
        {isSearching && (
                  <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                    <span className="ml-2">Searching for nearby rooms...</span>
                  </div>
                )}
                
                {error && (
                  <div className="p-4 mb-4 text-sm text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-950/30 rounded-lg border border-red-100 dark:border-red-900/40" role="alert">
                    {error}
                  </div>
                )}
                
                {!isSearching && nearbyRooms.length === 0 && !error && (
                  <div className="text-center py-6">
                    <p>Searching for nearby available rooms...</p>
                  </div>
                )}
                
                {nearbyRooms.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {nearbyRooms.map((room, index) => (
                      <div 
                        key={index} 
                        onClick={() => handleRoomSelectAndClose(room.building, room.room)}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-primary dark:hover:border-primary cursor-pointer transition-colors bg-white dark:bg-gray-800 hover:bg-primary/5 dark:hover:bg-primary/10"
                      >
                        <div className="flex items-center mb-2">
                          <div className={`w-3 h-3 rounded-full mr-2 ${
                            room.availabilityStatus === 'available-full-day' 
                              ? 'bg-green-500' 
                              : 'bg-yellow-500'
                          }`}></div>
                          <h4 className="font-medium text-gray-900 dark:text-gray-100">Building {room.building}, Room {room.room}</h4>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                          {room.buildingName} • {Math.round(room.distance)} meters away
                        </p>
                        <p className="text-sm font-medium mt-1 text-gray-800 dark:text-gray-200">
                          {room.availabilityStatus === 'available-full-day' 
                            ? 'Available all day' 
                            : 'Available now, but has upcoming exam(s)'}
                        </p>
                        {room.availabilityStatus === 'available-now' && room.upcomingExams.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500 dark:text-gray-400">Next exam at {room.upcomingExams[0].time.split(' - ')[0]}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Close
                </Button>
                <Button color="primary" onPress={() => findNearbyRooms(500)} isLoading={isSearching}>
                  Refresh
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default NearbyRoomsModal;